import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import Flex from "@/components/Flex";
import MaskMultiSelect from "@/components/MaskMultiSelect";
import Text from "@/components/Text";
import type { Option } from "@/constants/common";
import { INTAKE_FORM_SECTIONS } from "@/pages/app/provider/scheduling/appointment";
import {
  GROUP_FIELDS_LABEL,
  GROUP_FORMS_ICON_STYLE,
  GROUP_FORMS_LIST_STYLE,
} from "../group-visit-constant";
import { GroupFieldsValues } from "../group-visit-types";
import { useAssessmentOptions } from "../hooks/use-assessment-options";

export type GroupFormsSectionProps = {
  formik: FormikProps<GroupFieldsValues>;
  disabled?: boolean;
};

const SelectedList = ({ options }: { options: Option[] }) => (
  <ul style={GROUP_FORMS_LIST_STYLE}>
    {options.map((option) => (
      <li key={option.value}>
        <Text color="grey">{option.label}</Text>
      </li>
    ))}
  </ul>
);

/** "Forms will be emailed" block, mirroring the individual Schedule Appointment form. */
const GroupFormsSection = ({ formik, disabled }: GroupFormsSectionProps) => {
  const { values, setFieldValue } = formik;
  const { assessmentOptions, onSearchAssessments } = useAssessmentOptions();

  const handleSendFormsChange = (checked: boolean) => {
    setFieldValue("sendForms", checked);
    if (!checked) {
      setFieldValue("customForms", []);
      setFieldValue("assessments", []);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Flex alignItems="center">
          <FormControlLabel
            name="sendForms"
            control={<Checkbox />}
            label={
              <Flex alignItems="center" gap={0.5}>
                <DescriptionOutlinedIcon sx={GROUP_FORMS_ICON_STYLE} />
                <Text fontWeight={500}>{GROUP_FIELDS_LABEL.sendForms}</Text>
              </Flex>
            }
            checked={values.sendForms}
            disabled={disabled}
            onChange={(_, checked) => handleSendFormsChange(checked)}
          />
        </Flex>
      </Grid>

      {values.sendForms && (
        <>
          <Grid
            container
            sx={{
              alignContent: "flex-start",
            }}
            size={6}
          >
            <Grid size={12}>
              <MaskMultiSelect
                label={GROUP_FIELDS_LABEL.intakeForms}
                options={INTAKE_FORM_SECTIONS}
                optionWithCheckbox
                disabled={disabled}
                value={values.customForms}
                onChange={(selected) =>
                  setFieldValue("customForms", selected ?? [])
                }
              />
            </Grid>
            <Grid size={12}>
              <SelectedList options={values.customForms} />
            </Grid>
          </Grid>

          <Grid
            container
            sx={{
              alignContent: "flex-start",
            }}
            size={6}
          >
            <Grid size={12}>
              <MaskMultiSelect
                label={GROUP_FIELDS_LABEL.assessments}
                options={assessmentOptions}
                optionWithCheckbox
                disabled={disabled}
                value={values.assessments}
                onSearch={onSearchAssessments}
                onChange={(selected) =>
                  setFieldValue("assessments", selected ?? [])
                }
              />
            </Grid>
            <Grid size={12}>
              <SelectedList options={values.assessments} />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default GroupFormsSection;
